import { DownloadOutlined, FilterOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, Form, Space, Table, Typography, Row, Col, Statistic, Tooltip, Tag } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface SalesReportData {
	retailer_id: number;
	retailer_name: string;
	vendor_id: string;
	sold_count: number;
	completed_count: number;
	recovered_count: number;
	pending_count: number;
	total_installment_amount: number;
	total_zf_paid_amount: number;
	total_installment_paid_amount: number;
	total_zazu_fee: number;
	balance_amount: number;
	overdue_amount: number;
}

interface SalesReportTotals {
	retailer_id: string;
	retailer_name: string;
	vendor_id: string;
	sold_count: number;
	completed_count: number;
	recovered_count: number;
	pending_count: number;
	total_installment_amount: number;
	total_zf_paid_amount: number;
	total_installment_paid_amount: number;
	total_zazu_fee: number;
	balance_amount: number;
	overdue_amount: number;
}

interface SalesReportResponse {
	retailers: SalesReportData[];
	totals: SalesReportTotals;
	count: number;
	page: number;
	limit: number;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const ugx = (value: number) => `UGX ${formatNumber(value)}`;

const withTooltip = (title: string, tooltip: string) => (
	<span>
		{title}{' '}
		<Tooltip title={tooltip}>
			<InfoCircleOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
		</Tooltip>
	</span>
);

const sortableColumn = <T extends SalesReportData>(
	overrides: Partial<ColumnType<T>> & { dataIndex: keyof T }
): ColumnType<T> => ({
	sorter: (a: T, b: T) => (a[overrides.dataIndex] as number) - (b[overrides.dataIndex] as number),
	align: 'right' as const,
	...overrides,
});

const SalesReport = () => {
	const navigate = useNavigate();
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({ page: 1, pageSize: 20 });
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs().startOf('year'), dayjs()],
	});

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['sales-report', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};
			const response = await axios.get('/admin/reports/sales', { withCredentials: true, params });
			return response.data.results as SalesReportResponse;
		},
	});

	if (error) handleAPIError(error);

	const salesData: SalesReportData[] = data?.retailers || [];
	const totals = data?.totals;

	const handleFilter = () => {
		setFilters(form.getFieldsValue());
		setPaginationConfig({ ...paginationConfig, page: 1 });
	};

	const handleReset = () => {
		form.resetFields();
		setFilters({ date_range: [dayjs().startOf('year'), dayjs()] });
		setPaginationConfig({ page: 1, pageSize: 20 });
	};

	const applyPreset = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
		const range: [dayjs.Dayjs, dayjs.Dayjs] = [start, end];
		form.setFieldsValue({ date_range: range });
		setFilters({ date_range: range });
		setPaginationConfig({ ...paginationConfig, page: 1 });
	};

	const handleExport = () => {
		if (!salesData.length) return;

		const rows = [
			...salesData.map((row) => ({
				'Retailer Name': row.retailer_name,
				'Vendor ID': row.vendor_id,
				'Sold': row.sold_count,
				'Completed': row.completed_count,
				'Pending': row.pending_count,
				'Total Repayable (UGX)': row.total_installment_amount,
				'Paid to Retailer (UGX)': row.total_zf_paid_amount,
				'Collected (UGX)': row.total_installment_paid_amount,
				'Zazu Fee (UGX)': row.total_zazu_fee,
				'Balance (UGX)': row.balance_amount,
				'Overdue (UGX)': row.overdue_amount,
			})),
			// Totals row
			...(totals
				? [{
					'Retailer Name': 'TOTALS',
					'Vendor ID': '',
					'Sold': totals.sold_count,
					'Completed': totals.completed_count,
					'Pending': totals.pending_count,
					'Total Repayable (UGX)': totals.total_installment_amount,
					'Paid to Retailer (UGX)': totals.total_zf_paid_amount,
					'Collected (UGX)': totals.total_installment_paid_amount,
					'Zazu Fee (UGX)': totals.total_zazu_fee,
					'Balance (UGX)': totals.balance_amount,
					'Overdue (UGX)': totals.overdue_amount,
				}]
				: []),
		];

		const worksheet = XLSX.utils.json_to_sheet(rows);

		// Column widths
		worksheet['!cols'] = [
			{ wch: 25 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 10 },
			{ wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
		];

		const workbook = XLSX.utils.book_new();
		const dateRange = filters.date_range
			? `${filters.date_range[0].format('YYYY-MM-DD')} to ${filters.date_range[1].format('YYYY-MM-DD')}`
			: dayjs().format('YYYY-MM-DD');
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');
		XLSX.writeFile(workbook, `sales-report-${dateRange}.xlsx`);
	};

	const drillDown = (row: SalesReportData, filter: 'sold' | 'completed' | 'pending' | 'recovered') => {
		const start = filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : dayjs().startOf('year').format('YYYY-MM-DD');
		const end = filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
		navigate(
			`/admin/reports/sales/devices?retailer_id=${row.retailer_id}&retailer_name=${encodeURIComponent(row.retailer_name)}&filter=${filter}&start=${start}&end=${end}`
		);
	};

	const columns: ColumnsType<SalesReportData> = [
		{
			title: 'Retailer Name',
			dataIndex: 'retailer_name',
			key: 'retailer_name',
			width: 180,
			fixed: 'left',
			sorter: (a, b) => a.retailer_name.localeCompare(b.retailer_name),
		},
		{
			title: 'Vendor ID',
			dataIndex: 'vendor_id',
			key: 'vendor_id',
			width: 110,
		},
		sortableColumn({
			title: 'Sold',
			dataIndex: 'sold_count',
			key: 'sold_count',
			width: 80,
			align: 'center',
			render: (v: number, row) => (
				<Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => drillDown(row, 'sold')}>
					{v}
				</Button>
			),
		}),
		sortableColumn({
			title: 'Completed',
			dataIndex: 'completed_count',
			key: 'completed_count',
			width: 100,
			align: 'center',
			render: (v: number, row) => (
				<Button type="link" style={{ padding: 0, color: '#52c41a' }} onClick={() => drillDown(row, 'completed')}>
					{v}
				</Button>
			),
		}),
		sortableColumn({
			title: 'Recovered',
			dataIndex: 'recovered_count',
			key: 'recovered_count',
			width: 100,
			align: 'center',
			render: (v: number, row) => (
				<Button type="link" style={{ padding: 0, color: '#595959' }} onClick={() => drillDown(row, 'recovered')}>
					{v}
				</Button>
			),
		}),
		sortableColumn({
			title: 'Pending',
			dataIndex: 'pending_count',
			key: 'pending_count',
			width: 90,
			align: 'center',
			render: (v: number, row) => (
				<Button type="link" style={{ padding: 0, color: '#faad14' }} onClick={() => drillDown(row, 'pending')}>
					{v}
				</Button>
			),
		}),
		sortableColumn({
			title: withTooltip('Total Repayable', 'Total amount customers are required to pay back (device price + interest)'),
			dataIndex: 'total_installment_amount',
			key: 'total_installment_amount',
			width: 170,
			render: (v: number) => ugx(v),
		}),
		sortableColumn({
			title: withTooltip('Paid to Retailer', 'Amount Zazu has disbursed to the retailer upfront (Zazu Funded sales)'),
			dataIndex: 'total_zf_paid_amount',
			key: 'total_zf_paid_amount',
			width: 160,
			render: (v: number) => ugx(v),
		}),
		sortableColumn({
			title: withTooltip('Collected', 'Total amount customers have paid so far'),
			dataIndex: 'total_installment_paid_amount',
			key: 'total_installment_paid_amount',
			width: 150,
			render: (v: number) => ugx(v),
		}),
		sortableColumn({
			title: withTooltip('Zazu Fee', "Zazu's earnings on these sales"),
			dataIndex: 'total_zazu_fee',
			key: 'total_zazu_fee',
			width: 140,
			render: (v: number) => ugx(v),
		}),
		sortableColumn({
			title: withTooltip('Balance', 'Amount customers still owe (Total Repayable − Collected)'),
			dataIndex: 'balance_amount',
			key: 'balance_amount',
			width: 150,
			render: (v: number) => {
				if (v < 0) return <Tag color="orange">UGX {formatNumber(Math.abs(v))} overpaid</Tag>;
				return ugx(v);
			},
		}),
		sortableColumn({
			title: withTooltip('Overdue', 'Portion of the balance that is past its due date'),
			dataIndex: 'overdue_amount',
			key: 'overdue_amount',
			width: 150,
			defaultSortOrder: 'descend' as SortOrder,
			render: (v: number) =>
				v > 0 ? (
					<Text strong style={{ color: '#ff4d4f' }}>
						UGX {formatNumber(v)}
					</Text>
				) : (
					<Text style={{ color: '#52c41a' }}>—</Text>
				),
		}),
	];

	const completionRate =
		totals && totals.sold_count > 0
			? Math.round((totals.completed_count / totals.sold_count) * 100)
			: 0;

	const collectionRate =
		totals && totals.total_installment_amount > 0
			? Math.round((totals.total_installment_paid_amount / totals.total_installment_amount) * 100)
			: 0;

	return (
		<AppLayout
			title="Sales Report"
			subtitle="Retailer sales performance and installment collection overview"
		>
			{/* Summary Statistics */}
			{totals && (
				<Card className="mb-6">
					<Title level={5} className="mb-4">
						Sales Summary
					</Title>
					<Row gutter={[24, 24]}>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Devices Sold"
								value={totals.sold_count}
								valueStyle={{ color: '#1890ff' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Completed"
								value={totals.completed_count}
								suffix={<Text style={{ fontSize: 13, color: '#8c8c8c' }}>({completionRate}%)</Text>}
								valueStyle={{ color: '#52c41a' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Pending"
								value={totals.pending_count}
								valueStyle={{ color: '#faad14' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Repayable"
								value={totals.total_installment_amount}
								formatter={(v) => `UGX ${formatNumber(v as number)}`}
								valueStyle={{ color: '#722ed1', fontSize: 16 }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title={
									<span>
										Collected{' '}
										<Text style={{ fontSize: 12, color: '#8c8c8c' }}>({collectionRate}%)</Text>
									</span>
								}
								value={totals.total_installment_paid_amount}
								formatter={(v) => `UGX ${formatNumber(v as number)}`}
								valueStyle={{ color: '#13c2c2', fontSize: 16 }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Overdue"
								value={totals.overdue_amount}
								formatter={(v) => `UGX ${formatNumber(v as number)}`}
								valueStyle={{ color: totals.overdue_amount > 0 ? '#ff4d4f' : '#52c41a', fontSize: 16 }}
							/>
						</Col>
					</Row>
				</Card>
			)}

			{/* Filters */}
			<Card className="mb-4">
				<Form
					form={form}
					layout="inline"
					initialValues={{ date_range: [dayjs().startOf('year'), dayjs()] }}
					className="gap-4"
				>
					<Form.Item name="date_range" label="Date Range">
						<RangePicker style={{ width: 300 }} placeholder={['Start Date', 'End Date']} />
					</Form.Item>
					<Form.Item>
						<Space wrap>
							<Button size="small" onClick={() => applyPreset(dayjs().startOf('month'), dayjs())}>
								This Month
							</Button>
							<Button size="small" onClick={() => applyPreset(dayjs().subtract(3, 'month'), dayjs())}>
								Last 3 Months
							</Button>
							<Button size="small" onClick={() => applyPreset(dayjs().startOf('year'), dayjs())}>
								This Year
							</Button>
						</Space>
					</Form.Item>
					<Form.Item>
						<Space>
							<Button type="primary" icon={<FilterOutlined />} onClick={handleFilter}>
								Apply Filters
							</Button>
							<Button icon={<ReloadOutlined />} onClick={handleReset}>
								Reset
							</Button>
							<Button icon={<DownloadOutlined />} onClick={handleExport}>
								Export
							</Button>
							<Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
								Refresh
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Card>

			{/* Data Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={salesData}
					loading={isLoading}
					rowKey="retailer_id"
					scroll={{ x: 1500 }}
					pagination={{
						current: paginationConfig.page,
						pageSize: paginationConfig.pageSize,
						total: data?.count || 0,
						onChange: (page, pageSize) => setPaginationConfig({ page, pageSize: pageSize || 20 }),
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} retailers`,
					}}
					summary={() =>
						totals ? (
							<Table.Summary fixed>
								<Table.Summary.Row style={{ fontWeight: 600, background: '#fafafa' }}>
									<Table.Summary.Cell index={0} colSpan={2}>
										Totals
									</Table.Summary.Cell>
									<Table.Summary.Cell index={2} align="center">
										{totals.sold_count}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={3} align="center" className="text-green-600">
										{totals.completed_count}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={4} align="center">
										{totals.recovered_count}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={5} align="center">
										{totals.pending_count}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={6} align="right">
										{ugx(totals.total_installment_amount)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={7} align="right">
										{ugx(totals.total_zf_paid_amount)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={8} align="right">
										{ugx(totals.total_installment_paid_amount)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={9} align="right">
										{ugx(totals.total_zazu_fee)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={10} align="right">
										{ugx(totals.balance_amount)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={11} align="right">
										<Text strong style={{ color: totals.overdue_amount > 0 ? '#ff4d4f' : undefined }}>
											{ugx(totals.overdue_amount)}
										</Text>
									</Table.Summary.Cell>
								</Table.Summary.Row>
							</Table.Summary>
						) : null
					}
				/>
			</Card>
		</AppLayout>
	);
};

export default SalesReport;
